import React, { useState, useEffect } from 'react';

// ⚠️ THIS LIST IS FIXED, AND Dashboard.js COMPUTES hasErrors OVER ALL STEP
// VALUES. A step the backend reports and this list does not name flips the pane
// to "Creation Failed" with no failed row visible to explain it. So a new
// onboarding step is added HERE and to CREATION_LABELS in the same change as the
// backend one. `roster` arrived with T538 (the primary contact goes on the
// project roster at Won) - see
// Planning/PRIMARY-CONTACT-IDENTITY-2026-09-02.md.
const CREATION_STEPS = ['project', 'tasks', 'wiki', 'cognitoUser', 'email', 'roster'];
const CREATION_LABELS = {
    project: 'Project record',
    tasks: 'Tasks',
    wiki: 'Wiki pages',
    cognitoUser: 'Client account',
    email: 'Welcome email',
    roster: 'Project roster'
};

// ⚠️ `roster` IS ROLLED BACK, and it has to be. An earlier version of this file
// said the opposite. Since T538 the primary's members row is written AT WON, so
// a rolled-back project has one — and if the rollback deleted the Cognito
// account and the project but left that row, the result would be a members row
// with no account and no project, which no repair script can reach. It is only
// left alone by `scripts/repair-primary-roster.js`, which repairs LIVE projects.
//
// `isRollbackComplete` iterates every value in rollbackResults, so a step the
// backend reports and this list does not name would also be invisible while
// still counting towards completion.
const ROLLBACK_STEPS = ['cognitoUser', 'wiki', 'tasks', 'roster', 'project'];
const ROLLBACK_LABELS = {
    cognitoUser: 'Client account',
    wiki: 'Wiki pages',
    tasks: 'Tasks',
    roster: 'Project roster',
    project: 'Project record'
};

const CreationProgressPane = ({
    isOpen,
    results,
    isComplete,
    hasErrors,
    clientName,
    isRollingBack,
    rollbackResults,
    onRollback,
    onClose,
    tempPassword
}) => {
    const [expandedErrors, setExpandedErrors] = useState({});
    const [autoCloseTimer, setAutoCloseTimer] = useState(null);

    // Auto-close on success after 3.5 seconds - NEVER when credentials are
    // displayed: the once-only password must not vanish on a timer
    useEffect(() => {
        if (isComplete && !hasErrors && !isRollingBack && !rollbackResults && !tempPassword) {
            const timer = setTimeout(() => {
                onClose();
            }, 3500);
            setAutoCloseTimer(timer);
            return () => clearTimeout(timer);
        }
    }, [isComplete, hasErrors, isRollingBack, rollbackResults, onClose, tempPassword]);

    // Clear auto-close timer if there are errors
    useEffect(() => {
        if (hasErrors && autoCloseTimer) {
            clearTimeout(autoCloseTimer);
            setAutoCloseTimer(null);
        }
    }, [hasErrors, autoCloseTimer]);

    if (!isOpen) return null;

    const toggleError = (key) => {
        setExpandedErrors(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
            case 'displayed':
                return <span className="text-green-600 text-xl">&#x2713;</span>;
            case 'failed':
                return <span className="text-red-600 text-xl">&#x2717;</span>;
            case 'skipped':
                return <span className="text-gray-400 text-xl">&#x2013;</span>;
            case 'pending':
            default:
                return (
                    <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                );
        }
    };

    const renderStepRow = (key, result, label) => (
        <div key={key}>
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className="w-6 flex justify-center">
                        {getStatusIcon(result.status)}
                    </div>
                    <span className={`${result.status === 'failed' ? 'text-red-700' : 'text-gray-700'}`}>
                        {label}
                    </span>
                </div>
                <div className="flex items-center space-x-2">
                    {result.status === 'success' && result.count > 0 && (
                        <span className="text-sm text-gray-500">({result.count})</span>
                    )}
                    {result.status === 'failed' && result.error && (
                        <button
                            onClick={() => toggleError(key)}
                            className="text-sm text-red-600 hover:text-red-800"
                        >
                            {expandedErrors[key] ? 'Hide' : 'Details'}
                        </button>
                    )}
                </div>
            </div>

            {/* Error details */}
            {expandedErrors[key] && result.error && (
                <div className="ml-9 mt-1 p-2 bg-red-50 rounded text-sm text-red-700 font-mono">
                    {result.error}
                </div>
            )}
        </div>
    );

    const isRollbackComplete = rollbackResults && Object.values(rollbackResults).every(r => r.status !== 'pending');
    const showRollbackMode = isRollingBack || rollbackResults;

    // Determine header state
    let headerText;
    let headerBgClass = 'bg-blue-600';

    if (showRollbackMode) {
        if (isRollbackComplete) {
            headerText = 'Rollback Complete';
            headerBgClass = 'bg-green-600';
        } else {
            headerText = 'Rolling back...';
        }
    } else if (isComplete) {
        if (hasErrors) {
            headerText = 'Creation Failed';
            headerBgClass = 'bg-orange-600';
        } else {
            headerText = 'Project Created';
            headerBgClass = 'bg-green-600';
        }
    } else {
        headerText = `Creating: ${clientName}`;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                {/* Header */}
                <div className={`px-6 py-4 rounded-t-lg ${headerBgClass} text-white`}>
                    <h2 className="text-xl font-bold">{headerText}</h2>
                </div>

                <div className="p-6">
                    {/* Progress list */}
                    <div className="space-y-3">
                        {showRollbackMode ? (
                            // Rollback steps
                            ROLLBACK_STEPS.map(key => {
                                const result = rollbackResults?.[key] || { status: 'pending', count: 0, error: null };
                                return renderStepRow(key, result, ROLLBACK_LABELS[key]);
                            })
                        ) : (
                            // Creation steps
                            CREATION_STEPS.map(key => {
                                const result = results?.[key] || { status: 'pending', count: 0, error: null };
                                const label = key === 'email' && result.status === 'displayed'
                                    ? 'Credentials displayed'
                                    : CREATION_LABELS[key];
                                return renderStepRow(key, result, label);
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {(isComplete || isRollbackComplete) && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                            {/* Success message with auto-close countdown */}
                            {isComplete && !hasErrors && !showRollbackMode && (
                                <p className="text-sm text-green-700 mb-4">
                                    {tempPassword
                                        ? 'Project created successfully.'
                                        : 'Project created successfully. This window will close automatically.'}
                                </p>
                            )}

                            {/* Internal onboarding: once-only credentials display.
                                Props/state only - never persisted anywhere. */}
                            {isComplete && !hasErrors && !showRollbackMode && tempPassword && (
                                <>
                                    <p className="text-sm text-gray-700 mb-2 font-medium">
                                        Temporary password — shown once. Copy it now. Not emailed, not stored.
                                    </p>
                                    <div className="flex items-center gap-2 mb-4">
                                        <code className="flex-1 px-3 py-2 bg-gray-100 rounded font-mono text-sm select-all">
                                            {tempPassword}
                                        </code>
                                        <button
                                            onClick={() => navigator.clipboard.writeText(tempPassword)}
                                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            onClick={onClose}
                                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Error message and rollback button */}
                            {hasErrors && !showRollbackMode && (
                                <>
                                    <p className="text-sm text-orange-700 mb-4">
                                        An error occurred during project creation. You can roll back to clean up any partial data.
                                    </p>
                                    <div className="flex justify-between">
                                        <button
                                            onClick={onRollback}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                                        >
                                            Rollback
                                        </button>
                                        <button
                                            onClick={onClose}
                                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Rollback complete - just show close button */}
                            {isRollbackComplete && (
                                <div className="flex justify-end">
                                    <button
                                        onClick={onClose}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreationProgressPane;
